import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import DayView from "./DayView";
import MonthView from "./MonthView";
import TaskDetails from "./TaskDetails";
import WeekView from "./WeekView";

const List = () => {
  // Reference
  const toast = useRef(null);

  // State for date selection and filters
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState(0); // 0: Day, 1: Week, 2: Month
  const [searchTerm, setSearchTerm] = useState("");
  const [developer, setDeveloper] = useState(null);
  const [taskType, setTaskType] = useState(null);
  const [projectFilter, setProjectFilter] = useState(null);

  // Data states
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailsVisible, setTaskDetailsVisible] = useState(false);

  // Dropdown options
  const [developerOptions, setDeveloperOptions] = useState([]);
  const [taskTypeOptions, setTaskTypeOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);

  // View types
  const viewOptions = [
    { label: "Day View", value: 0 },
    { label: "Week View", value: 1 },
    { label: "Month View", value: 2 },
  ];

  useEffect(() => {
    // Initial data loading
    fetchFilterData();
    fetchTasksData();
  }, []);

  // Update useEffect to refetch tasks when date changes
  useEffect(() => {
    fetchFilterData();
    fetchTasksData();
  }, [date]); // Add date as dependency to refetch when it changes

  const fetchFilterData = async () => {
    try {
      // Fetch developers (staff) data
      const staffResponse = await Http.get("/staff-department-wise/30"); // Assuming department ID 30 is for developers
      if (Array.isArray(staffResponse.data)) {
        const developersList = staffResponse.data.map((dev) => {
          const fullName = `${dev.first_name || ""} ${dev.last_name || ""}`.trim();
          return {
            id: dev.id,
            name: fullName || dev.name || `ID: ${dev.id}`,
          };
        });
        setDevelopers(developersList);
        setDeveloperOptions([{ label: "All Developers", value: null }, ...developersList.map((dev) => ({ label: dev.name, value: dev.id }))]);
      }

      // Fetch task types
      const taskTypeResponse = await Http.get("/task-type");
      if (taskTypeResponse.data && taskTypeResponse.data.data) {
        const taskTypesList = taskTypeResponse.data.data;
        setTaskTypes(taskTypesList);
        setTaskTypeOptions([{ label: "All Task Types", value: null }, ...taskTypesList.map((type) => ({ label: type.task_type, value: type.id }))]);
      }

      // Fetch projects
      const projectsResponse = await Http.get("/project");
      if (projectsResponse.data && projectsResponse.data.data) {
        const projectsList = projectsResponse.data.data;
        setProjects(projectsList);
        setProjectOptions([{ label: "All Projects", value: null }, ...projectsList.map((project) => ({ label: project.title, value: project.id }))]);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load filter data",
        life: 3000,
      });
    }
  };

  const fetchTasksData = async () => {
    setLoading(true);
    try {
      // Use a timezone-safe approach to format the date (avoids off-by-one errors due to timezone conversion)
      const d = new Date(date);
      const formattedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      // console.log("Fetching tasks for date:", formattedDate);

      // Update API path to match the new backend route
      const response = await Http.get(`/tasks/calendar?date=${formattedDate}`);

      // console.log("API Response:", response);

      if (response.data && response.data.data) {
        // console.log("Setting tasks from response data:", response.data.data);
        setTasks(response.data.data);
      } else if (response.data) {
        // Handle case where data might be directly in the response
        // console.log("Setting tasks directly from response:", response.data);
        setTasks(response.data);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load tasks. " + (error.response?.data?.message || error.message),
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter tasks based on the current filter settings
  const getFilteredTasks = () => {
    // Use the tasks directly fetched from the API (which are already date-filtered)
    return tasks.filter((task) => {
      // Filter by search term
      const matchesSearch =
        !searchTerm ||
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()));

      // Filter by developer
      const matchesDeveloper = !developer || (task.developers && task.developers.some((dev) => dev.id === developer));

      // Filter by task type
      const matchesTaskType = !taskType || task.task_type_id === taskType;

      // Filter by project
      const matchesProject = !projectFilter || task.project_id === projectFilter;

      // Return based on non-date filters
      return matchesSearch && matchesDeveloper && matchesTaskType && matchesProject;
    });
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setTaskDetailsVisible(true);
  };

  const renderCalendarView = () => {
    const filteredTasks = getFilteredTasks();

    if (loading) {
      return (
        <div className="text-center p-5">
          <ProgressSpinner />
          <p className="mt-3">Loading task data...</p>
        </div>
      );
    }

    switch (view) {
      case 0:
        return <DayView date={date} tasks={filteredTasks} onTaskClick={handleTaskClick} />;
      case 1:
        return <WeekView date={date} tasks={filteredTasks} onTaskClick={handleTaskClick} />;
      case 2:
        return <MonthView date={date} tasks={filteredTasks} onTaskClick={handleTaskClick} />;
      default:
        return <DayView date={date} tasks={filteredTasks} onTaskClick={handleTaskClick} />;
    }
  };

  return (
    <div>
      <IsLogin />
      <Toast ref={toast} />

      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Task Calendar</h5>
          <div className="d-flex justify-content-between align-items-center w-100 mt-2">
            <div className="d-flex align-items-center">
              <span className="p-input-icon-left mr-3" style={{ width: "300px" }}>
                <i className="pi pi-search" style={{ marginLeft: "4px" }} />
                <InputText
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search tasks..."
                  className="w-100"
                  style={{ paddingLeft: "20px" }}
                />
              </span>
              <Dropdown value={view} options={viewOptions} onChange={(e) => setView(e.value)} className="mx-2" />
              <Calendar value={date} onChange={(e) => setDate(e.value)} showIcon className="mx-2" />
            </div>
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card p-3">
            <div className="filter-section mb-3 d-flex flex-wrap">
              <div className="filter-item p-2" style={{ minWidth: "200px" }}>
                <label className="mb-1">Developer</label>
                <Dropdown
                  value={developer}
                  options={developerOptions}
                  onChange={(e) => setDeveloper(e.value)}
                  placeholder="Select Developer"
                  className="w-100"
                />
              </div>
              <div className="filter-item p-2" style={{ minWidth: "200px" }}>
                <label className="mb-1">Task Type</label>
                <Dropdown
                  value={taskType}
                  options={taskTypeOptions}
                  onChange={(e) => setTaskType(e.value)}
                  placeholder="Select Task Type"
                  className="w-100"
                />
              </div>
              <div className="filter-item p-2" style={{ minWidth: "200px" }}>
                <label className="mb-1">Project</label>
                <Dropdown
                  value={projectFilter}
                  options={projectOptions}
                  onChange={(e) => setProjectFilter(e.value)}
                  placeholder="Select Project"
                  className="w-100"
                />
              </div>
              <div className="filter-item p-2 d-flex align-items-end">
                <Button
                  icon="pi pi-refresh"
                  className="p-button-outlined"
                  onClick={() => {
                    setSearchTerm("");
                    setDeveloper(null);
                    setTaskType(null);
                    setProjectFilter(null);
                    setDate(new Date());
                  }}
                  tooltip="Reset Filters"
                  tooltipOptions={{ position: "bottom" }}
                />
              </div>
            </div>

            <div className="calendar-container">{renderCalendarView()}</div>
          </div>
        </div>
      </div>

      <Dialog
        header="Task Details"
        visible={taskDetailsVisible}
        style={{ width: "80%", maxWidth: "800px" }}
        onHide={() => setTaskDetailsVisible(false)}
        dismissableMask
      >
        {selectedTask && <TaskDetails task={selectedTask} />}
      </Dialog>
    </div>
  );
};

export default List;
